import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AutoComplete,
  Button,
  Drawer,
  Dropdown,
  Input,
  Modal,
  Table,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { MenuProps } from "antd";
import {
  CheckCircleFilled,
  CloseOutlined,
  DownOutlined,
  EditOutlined,
  EllipsisOutlined,
  FullscreenOutlined,
  HistoryOutlined,
  InfoCircleFilled,
  PlusOutlined,
  StopOutlined,
  UpOutlined,
  UsergroupAddOutlined,
  UserOutlined,
} from "@ant-design/icons";

import GuideButton from "@/components/UI/GuideButton";
import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import RoleSelect from "@/components/Admin/RoleSelect";
import AppliedFiltersSection from "@/components/UI/TableToolbar/Filtering/AppliedFiltersSection";
import PageHeader from "@/components/UI/PageHeader";
import FilterButton from "@/components/UI/TableToolbar/Filtering/FilterButton";
import type { SelectedFilters } from "@/components/UI/TableToolbar/Filtering/filterTypes";
import {
  useAdminManagedTrials,
  useAdminManagedUsers,
  useCreateManagedUser,
  useDeleteManagedUser,
  useUpdateManagedUser,
} from "@/hooks/useAdminManageUsers";
import EditUserSettingsModal from "./EditUserSettingsModal";
import DeactivateUserModal from "./DeactivateUserModal";
import { useManagedUserEditFlow } from "./useManagedUserEditFlow";
import BatchInviteUsersModal, {
  type BatchInvitePayload,
  type BatchInviteSubmitResult,
} from "./BatchInviteUsersModal";
import {
  type AdminStudyPermissionAccess,
  type AdminUser,
} from "./adminUsersAccessData";
import {
  adminAccountHistoryByAssignmentId,
  adminRevokeStudyAccessContent,
  type AdminAccountHistory,
  type AdminAccountHistoryEvent,
  type AdminStudyAssignment,
  type AdminStudyPermissionGroup,
} from "./adminRolesPermissionsData";
import {
  buildCreateManagedUserPayload,
  buildStudyOptionsFromManagedTrials,
  buildTrialRolesById,
  formatRoleLabel,
  getAdminUserDisplayName,
  getRoleDotColor,
  mapManagedTrialsToStudyPermissionGroups,
  mapManagedUsersToAdminUsers,
} from "./adminRolesPermissionsMappers";
import AdminDownloadMenu from "./AdminDownloadMenu";

// TODO: Account history is out of scope for the first release. Flip this flag
// to restore the row action and drawer once the audit endpoint ships.
const ACCOUNT_HISTORY_ENABLED = false;

const ROLES_PERMISSIONS_TOOLTIP =
  "Assign each user’s study access and role here. Invite or deactivate people in Users & Access. Admins can manage people and permissions, but they do not get study data access by default.";

type AddUserToStudyModalState = {
  study: AdminStudyPermissionGroup;
} | null;

type AssignmentActionState = {
  assignment: AdminStudyAssignment;
  study: AdminStudyPermissionGroup;
} | null;

const ROLES_FILTER_KEYS = {
  email: "email",
  accountType: "account_type",
  role: "role",
  trial: "trial",
} as const;

const ROLES_FILTER_LABELS: Record<string, string> = {
  [ROLES_FILTER_KEYS.email]: "Email",
  [ROLES_FILTER_KEYS.accountType]: "Account type",
  [ROLES_FILTER_KEYS.role]: "Role",
  [ROLES_FILTER_KEYS.trial]: "Trial",
};

const adminModalShellProps = {
  style: { top: 0, padding: 0, paddingBottom: 0 },
  wrapClassName: "no-padding-modal",
};

const studyDiamondBaseColors = [
  "#64BDE1",
  "#BEBEBE",
  "#FBD560",
  "#C275A1",
  "#5BB27F",
  "#E5AB5F",
];

function getStudyDiamondBaseColor(study: AdminStudyPermissionGroup) {
  const source = `${study.studyName} ${study.trialId}`;
  const colorIndex = [...source].reduce(
    (sum, character, index) => sum + character.charCodeAt(0) * (index + 1),
    0,
  );

  return studyDiamondBaseColors[colorIndex % studyDiamondBaseColors.length];
}

function mixHexWithWhite(hex: string, whiteAmount: number) {
  const normalizedHex = hex.replace("#", "");
  const rgb = [0, 2, 4].map((index) =>
    Number.parseInt(normalizedHex.slice(index, index + 2), 16),
  );
  const mixedRgb = rgb.map((channel) =>
    Math.round(channel + (255 - channel) * whiteAmount),
  );
  return `#${mixedRgb
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`;
}

function getStudyDiamondShades(study: AdminStudyPermissionGroup) {
  const baseColor = getStudyDiamondBaseColor(study);

  return {
    left: mixHexWithWhite(baseColor, 0.34),
    top: baseColor,
    right: mixHexWithWhite(baseColor, 0.7),
  };
}

function StudyIcon({ study }: { study: AdminStudyPermissionGroup }) {
  const shades = getStudyDiamondShades(study);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      <path d="M1.33948 15L1.33987 5L10 10V20L1.33948 15Z" fill={shades.left} />
      <path d="M1.33948 5L10 0L18.6602 5L10 10" fill={shades.top} />
      <path d="M10 10L18.6605 5V15L10 20" fill={shades.right} />
    </svg>
  );
}

function RoleBadge({ access }: { access: AdminStudyAssignment["access"] }) {
  return (
    <div className="flex items-center gap-1.5 text-sm text-[#0F1923]">
      <span className="flex h-4 w-4 items-center justify-center rounded-[3px] bg-[#D2EFF9]">
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: getRoleDotColor(access) }}
        />
      </span>
      <span>{formatRoleLabel(access)}</span>
    </div>
  );
}

type StudyModalUser = {
  id: string;
  displayName: string;
  email: string;
  isExistingUser: boolean;
  role?: AdminStudyPermissionAccess;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getUserDisplayName(user: AdminUser) {
  return getAdminUserDisplayName(user);
}

function createModalRowFromInput(
  rawValue: string,
  availableUsers: AdminUser[],
): StudyModalUser | null {
  const value = rawValue.trim();

  if (!value) {
    return null;
  }

  const existingUser = availableUsers.find((user) => {
    const displayName = getUserDisplayName(user).toLowerCase();
    const normalizedValue = value.toLowerCase();
    return (
      user.email.toLowerCase() === normalizedValue ||
      displayName === normalizedValue
    );
  });

  if (existingUser) {
    return {
      id: existingUser.id,
      displayName: getUserDisplayName(existingUser),
      email: existingUser.email,
      isExistingUser: true,
    };
  }

  if (!EMAIL_PATTERN.test(value)) {
    return null;
  }

  return {
    id: `new-${value.toLowerCase()}`,
    displayName: value,
    email: value,
    isExistingUser: false,
  };
}

function AddUserToStudyModal({
  modalState,
  users,
  onClose,
  onSubmit,
}: {
  modalState: AddUserToStudyModalState;
  users: AdminUser[];
  onClose: () => void;
  onSubmit: (
    users: StudyModalUser[],
    study: AdminStudyPermissionGroup,
  ) => Promise<void>;
}) {
  const [userInput, setUserInput] = useState("");
  const [modalUsers, setModalUsers] = useState<StudyModalUser[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const open = modalState !== null;
  const roles = modalState?.study.roles ?? [];
  const studyLabel = modalState
    ? `${modalState.study.studyName} | ${modalState.study.trialId}`
    : "";
  const assignedUserIds = new Set(
    modalState?.study.assignments.map((assignment) => assignment.user.id) ?? [],
  );
  const assignedUserEmails = new Set(
    modalState?.study.assignments.map((assignment) =>
      assignment.user.email.toLowerCase(),
    ) ?? [],
  );
  const pendingUserIds = new Set(modalUsers.map((user) => user.id));
  const pendingUserEmails = new Set(
    modalUsers.map((user) => user.email.toLowerCase()),
  );
  const inputTokens = userInput
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
  const lastInputToken = inputTokens[inputTokens.length - 1] ?? "";
  const isUserBlocked = (user: StudyModalUser) =>
    assignedUserIds.has(user.id) ||
    assignedUserEmails.has(user.email.toLowerCase()) ||
    pendingUserIds.has(user.id) ||
    pendingUserEmails.has(user.email.toLowerCase());
  const matchingUsers = users.filter((user) => {
    const normalizedToken = lastInputToken.toLowerCase();
    if (!normalizedToken) {
      return false;
    }
    if (
      assignedUserIds.has(user.id) ||
      assignedUserEmails.has(user.email.toLowerCase()) ||
      pendingUserIds.has(user.id) ||
      pendingUserEmails.has(user.email.toLowerCase())
    ) {
      return false;
    }
    return (
      getUserDisplayName(user).toLowerCase().includes(normalizedToken) ||
      user.email.toLowerCase().includes(normalizedToken)
    );
  });
  const inputCanBeAdded =
    inputTokens.length > 0 &&
    inputTokens.every((token) => {
      const parsedUser = createModalRowFromInput(token, users);
      return parsedUser !== null && !isUserBlocked(parsedUser);
    });
  const autocompleteOptions = matchingUsers.map((user) => ({
    value: getUserDisplayName(user),
    label: (
      <div className="flex flex-col">
        <span>{getUserDisplayName(user)}</span>
        <span className="text-xs text-[#00000073]">{user.email}</span>
      </div>
    ),
  }));
  const canSubmit =
    modalUsers.length > 0 && modalUsers.every((user) => Boolean(user.role));

  useEffect(() => {
    if (open) {
      setUserInput("");
      setModalUsers([]);
      setSubmitError(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const addUsersFromInput = () => {
    const parsedUsers = userInput
      .split(",")
      .map((value) => createModalRowFromInput(value, users))
      .filter(
        (user): user is StudyModalUser => user !== null && !isUserBlocked(user),
      );

    if (parsedUsers.length === 0) {
      return;
    }

    setModalUsers((currentUsers) => {
      const existingIds = new Set(currentUsers.map((user) => user.id));
      const seenIds = new Set<string>();
      const defaultRole = roles.length === 1 ? roles[0] : undefined;
      const uniqueNewUsers = parsedUsers
        .filter((user) => {
          if (existingIds.has(user.id) || seenIds.has(user.id)) {
            return false;
          }
          seenIds.add(user.id);
          return true;
        })
        .map((user) => ({
          ...user,
          role: user.role ?? defaultRole,
        }));
      return [...currentUsers, ...uniqueNewUsers];
    });
    setSubmitError(null);
    setUserInput("");
  };

  const removeUser = (userId: string) => {
    setModalUsers((currentUsers) =>
      currentUsers.filter((user) => user.id !== userId),
    );
  };

  const updateUserRole = (userId: string, role: AdminStudyPermissionAccess) => {
    setModalUsers((currentUsers) =>
      currentUsers.map((user) =>
        user.id === userId ? { ...user, role } : user,
      ),
    );
  };

  const resetAndCloseModal = () => {
    setUserInput("");
    setModalUsers([]);
    setSubmitError(null);
    setIsSubmitting(false);
    onClose();
  };

  const closeModal = () => {
    if (isSubmitting) {
      return;
    }

    resetAndCloseModal();
  };

  const handleSubmit = async () => {
    if (!modalState || !canSubmit || isSubmitting) {
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await onSubmit(modalUsers, modalState.study);
      resetAndCloseModal();
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Failed to add user to study.",
      );
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={closeModal}
      width={840}
      centered
      {...adminModalShellProps}
      footer={null}
      closeIcon={null}
      destroyOnHidden
      styles={
        {
          content: {
            padding: 0,
            borderRadius: 8,
            overflow: "hidden",
            boxShadow: "0px 4px 20px 0px rgba(0,0,0,0.10)",
          },
          body: { padding: 0 },
        } as any
      }
    >
      <div
        data-testid="add-user-to-study-modal"
        className="flex h-[710px] flex-col bg-white"
      >
        <div className="flex h-12 shrink-0 items-start border-b border-[#0000000f] pt-3">
          <div className="flex h-6 w-full items-center justify-between px-3">
            <h2 className="m-0 text-lg font-medium leading-none text-[#000000d9]">
              Add user to study
            </h2>
            <button
              type="button"
              aria-label="Close add user to study"
              className="flex h-5 w-5 items-center justify-center rounded text-[#20353E] hover:bg-[#EFF1F2]"
              onClick={closeModal}
            >
              <CloseOutlined />
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col bg-white px-3 py-3">
          <section className="flex flex-col gap-2">
            <h3 className="m-0 text-base font-medium leading-none tracking-[-0.16px] text-[#2B4752]">
              Select existing or add new users to study
            </h3>
            <div className="flex h-8 items-center gap-2">
              <AutoComplete
                value={userInput}
                options={autocompleteOptions}
                onChange={setUserInput}
                onSelect={(value) => setUserInput(value)}
                className="h-8 flex-1 [&_.ant-select-selector]:h-8! [&_.ant-select-selector]:rounded! [&_.ant-select-selection-search-input]:h-8!"
              >
                <Input
                  onPressEnter={addUsersFromInput}
                  placeholder="Enter emails separated by commas then hit enter or click add"
                  className="h-8 rounded border-[#0000001f] text-sm"
                  aria-label="Existing user name or new email"
                />
              </AutoComplete>
              <Button
                className="h-8 rounded border-[#00000073] px-3 font-medium text-[#2B4752]"
                disabled={!inputCanBeAdded}
                onClick={addUsersFromInput}
              >
                Add
              </Button>
            </div>
          </section>

          {modalUsers.length > 0 ? (
            <section className="mt-4 overflow-hidden rounded-lg bg-[#00000005]">
              <div className="grid grid-cols-[1fr_1fr_1fr_20px] gap-3 px-3 pb-1.5 pt-3 text-xs font-semibold uppercase leading-none tracking-[-0.12px] text-[#00000073]">
                <span>User</span>
                <span>Study</span>
                <span>Study Access Role</span>
                <span />
              </div>

              {modalUsers.map((user, index) => (
                <div
                  key={user.id}
                  className={`grid grid-cols-[1fr_1fr_1fr_20px] items-center gap-3 px-3 ${
                    index === modalUsers.length - 1
                      ? "pb-3 pt-2"
                      : "pb-2 pt-1.5"
                  }`}
                >
                  <div className="flex h-[35px] items-center gap-1.5">
                    {user.isExistingUser ? (
                      <AvatarWithInitials
                        name={user.displayName}
                        avatarSize={24}
                        avatarFontSize={12}
                        showLabel={false}
                      />
                    ) : (
                      <span className="flex h-6 w-6 items-center justify-center rounded-full border border-[#0000000a] bg-white text-[#00000040]">
                        <UserOutlined style={{ fontSize: 15 }} />
                      </span>
                    )}
                    <span className="text-sm leading-none tracking-[-0.14px] text-[#000000d9]">
                      {user.isExistingUser ? user.displayName : user.email}
                    </span>
                  </div>

                  <div className="flex h-[35px] items-center">
                    <span className="rounded bg-[#0000000a] px-1 py-1 text-sm font-medium leading-none tracking-[-0.14px] text-[#070B0D]">
                      {studyLabel}
                    </span>
                  </div>

                  <RoleSelect
                    value={user.role}
                    placeholder="Select role"
                    options={roles.map((option) => ({
                      label: formatRoleLabel(option),
                      value: option,
                    }))}
                    suffixIcon={<RightChevron />}
                    className="h-[35px] w-full [&_.ant-select-selector]:rounded-lg!"
                    onChange={(role) => updateUserRole(user.id, role)}
                  />

                  <button
                    type="button"
                    aria-label={`Remove ${user.isExistingUser ? user.displayName : user.email}`}
                    className="flex h-5 w-5 items-center justify-center rounded text-[#556C75] hover:bg-white"
                    onClick={() => removeUser(user.id)}
                  >
                    <CloseOutlined />
                  </button>
                </div>
              ))}
            </section>
          ) : null}
        </div>

        <div className="shrink-0 bg-white px-3 py-3">
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-2.5 rounded-lg border border-[#0000000a] bg-[#ECFAFF99] px-4 py-3 text-[#20353E]">
              <InfoCircleFilled className="mt-0.5 text-[#06709B]" />
              <p className="m-0 text-sm leading-[16px] tracking-[-0.14px]">
                New users who do not yet have a Vivo account will automatically
                receive the &quot;User&quot; account type when invited to in a
                study in Vivo. This account type can be modified later.
              </p>
            </div>
            {submitError ? (
              <p className="m-0 text-sm text-[#980000]" role="alert">
                {submitError}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex h-14 shrink-0 items-center justify-between border-t border-[#0000000f] p-3">
          <Button
            className="h-8 rounded border-[#00000073] px-3 font-medium text-[#2B4752]"
            disabled={isSubmitting}
            onClick={closeModal}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={!canSubmit || isSubmitting}
            loading={isSubmitting}
            className="h-8 rounded px-3 font-medium disabled:bg-[#0000001f]! disabled:text-[#00000040]!"
            onClick={handleSubmit}
          >
            Add to study
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function RightChevron() {
  return <span className="text-[#556C75]">›</span>;
}

function getAssignmentUserName(assignment: AdminStudyAssignment) {
  return getAdminUserDisplayName(assignment.user);
}

function buildPlaceholderAccountHistory({
  assignment,
  study,
  accountType,
}: {
  assignment: AdminStudyAssignment;
  study: AdminStudyPermissionGroup;
  accountType: "USER" | "ADMIN";
}): AdminAccountHistory {
  return {
    assignmentId: assignment.id,
    metadata: {
      user: assignment.user,
      email: assignment.user.email,
      studyLabel: `${study.studyName} | ${study.trialId}`,
      access: assignment.access,
      accountType,
    },
    events: [],
  };
}

function AccountBadge({ accountType }: { accountType: "USER" | "ADMIN" }) {
  return (
    <span className="rounded border border-[#CAE6D6] bg-[#F0FAF7] px-[3px] py-0.5 text-xs font-medium leading-none tracking-[-0.12px] text-[#004A42]">
      {accountType}
    </span>
  );
}

function StudyLabelBadge({
  label,
  dark = false,
}: {
  label: string;
  dark?: boolean;
}) {
  const [studyName, trialId] = label.split(" | ");
  return (
    <span
      className={`rounded px-1 py-1 text-xs leading-none tracking-[-0.12px] ${
        dark ? "bg-[#F9F9F733] text-[#F9F9F7]" : "bg-[#0000000a] text-[#070B0D]"
      }`}
    >
      <span className="font-medium">{studyName}</span>
      {trialId ? <span className="font-normal"> | {trialId}</span> : null}
    </span>
  );
}

function CompactRoleBadge({
  access,
  dark = false,
}: {
  access: AdminStudyPermissionAccess;
  dark?: boolean;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-sm leading-none tracking-[-0.14px] ${
        dark ? "text-[#F9F9F7]" : "text-[#000000d9]"
      }`}
    >
      <span className="flex h-4 w-4 items-center justify-center rounded-[3px] bg-[#D2EFF9]">
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: getRoleDotColor(access) }}
        />
      </span>
      {formatRoleLabel(access)}
    </span>
  );
}

function UserHistoryDrawer({
  history,
  open,
  onClose,
}: {
  history: AdminAccountHistory | null;
  open: boolean;
  onClose: () => void;
}) {
  const metadata = history?.metadata;
  const userName = metadata ? getAdminUserDisplayName(metadata.user) : "";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={566}
      closeIcon={null}
      styles={{
        body: { padding: 0, background: "#EFF1F2" },
        content: { boxShadow: "0px 4px 20px 3px rgba(0,0,0,0.05)" },
      }}
    >
      {history && metadata ? (
        <div
          data-testid="account-history-drawer"
          className="flex h-full flex-col bg-[#EFF1F2]"
        >
          <div className="flex flex-col gap-2 bg-[#162429] p-3 text-white">
            <div className="flex h-6 items-center justify-between">
              <h2 className="m-0 text-base font-semibold leading-none tracking-[-0.16px] text-white">
                Account&nbsp; History
              </h2>
              <div className="flex items-center gap-2 text-white">
                <button
                  type="button"
                  aria-label="Expand account history"
                  className="flex h-5 w-5 items-center justify-center rounded hover:bg-white/10"
                >
                  <FullscreenOutlined />
                </button>
                <button
                  type="button"
                  aria-label="Close account history"
                  className="flex h-5 w-5 items-center justify-center rounded hover:bg-white/10"
                  onClick={onClose}
                >
                  <CloseOutlined />
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-2 border-t border-white/10 px-1 pt-3">
              <div className="flex items-center justify-between text-sm tracking-[-0.14px]">
                <span className="text-[#D5DADC]">USER</span>
                <div className="flex items-center gap-2">
                  <AvatarWithInitials
                    name={userName}
                    avatarSize={24}
                    avatarFontSize={10.5}
                    showLabel={false}
                  />
                  <span className="font-medium text-white">{userName}</span>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm tracking-[-0.14px]">
                <span className="text-[#D5DADC]">EMAIL</span>
                <span className="text-white">{metadata.email}</span>
              </div>
              <div className="flex items-center justify-between text-sm tracking-[-0.14px]">
                <span className="text-[#D5DADC]">TRIAL</span>
                <StudyLabelBadge label={metadata.studyLabel} dark />
              </div>
              <div className="flex items-center justify-between text-sm tracking-[-0.14px]">
                <span className="text-[#D5DADC]">ROLE</span>
                <CompactRoleBadge access={metadata.access} dark />
              </div>
              <div className="flex items-center justify-between text-sm tracking-[-0.14px]">
                <span className="text-[#D5DADC]">ACCOUNT</span>
                <AccountBadge accountType={metadata.accountType} />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 px-4 pt-4">
            {history.events.length > 0 ? (
              history.events.map((event) => (
                <HistoryEventCard key={event.id} event={event} />
              ))
            ) : (
              <section className="rounded-lg bg-white px-4 py-3 text-sm text-[#556C75] shadow-[0px_2px_15px_3px_rgba(0,0,0,0.02)]">
                Account history is not available for this assignment yet.
              </section>
            )}
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}

function HistoryEventCard({ event }: { event: AdminAccountHistoryEvent }) {
  const userName = getAdminUserDisplayName(event.user);
  return (
    <section className="rounded-lg bg-white px-2 py-3 shadow-[0px_2px_15px_3px_rgba(0,0,0,0.02)]">
      <div className="flex items-center justify-between text-sm leading-none tracking-[-0.14px]">
        <div className="flex items-center gap-1">
          <span className="font-medium text-black">{event.actor}</span>
          <span className="text-[#2B4752]">{event.action}</span>
        </div>
        <span className="text-[#00000073]">{event.timestamp}</span>
      </div>
      <div className="mt-2 flex items-center border-l border-[#0000001f] px-2 text-sm leading-none tracking-[-0.14px] text-black">
        <div className="flex items-center gap-1">
          <AvatarWithInitials
            name={userName}
            avatarSize={20}
            avatarFontSize={10}
            showLabel={false}
          />
          <span>{userName}</span>
          <span>:</span>
          {event.studyLabel ? (
            <StudyLabelBadge label={event.studyLabel} />
          ) : null}
          {event.studyLabel && event.access ? <span>,</span> : null}
          {event.access ? <CompactRoleBadge access={event.access} /> : null}
          {event.accountType ? (
            <AccountBadge accountType={event.accountType} />
          ) : null}
        </div>
      </div>
    </section>
  );
}

function RevokeStudyAccessModal({
  actionState,
  onClose,
  onConfirm,
}: {
  actionState: AssignmentActionState;
  onClose: () => void;
  onConfirm: (
    assignment: AdminStudyAssignment,
    study: AdminStudyPermissionGroup,
  ) => Promise<void>;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const assignment = actionState?.assignment;
  const study = actionState?.study;
  const userName = assignment ? getAssignmentUserName(assignment) : "";
  const studyLabel = study ? `${study.studyName} | ${study.trialId}` : "";

  useEffect(() => {
    if (actionState) {
      setIsSubmitting(false);
      setSubmitError(null);
    }
  }, [actionState]);

  const handleConfirm = async () => {
    if (!assignment || !study || isSubmitting) {
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await onConfirm(assignment, study);
      setIsSubmitting(false);
      onClose();
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Failed to revoke study access.",
      );
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={actionState !== null}
      onCancel={onClose}
      width={740}
      centered
      {...adminModalShellProps}
      footer={null}
      closeIcon={null}
      destroyOnHidden
      styles={
        {
          content: {
            padding: 0,
            borderRadius: 8,
            overflow: "hidden",
            boxShadow: "0px 4px 20px 0px rgba(0,0,0,0.10)",
          },
          body: { padding: 0 },
        } as any
      }
    >
      {assignment && study ? (
        <div
          data-testid="revoke-study-access-modal"
          className="flex h-[314px] flex-col justify-between bg-white"
        >
          <div>
            <div className="flex h-12 items-start border-b border-[#0000000f] pt-3">
              <div className="flex h-6 w-full items-center justify-between px-3">
                <h2 className="m-0 text-lg font-medium leading-none text-[#000000d9]">
                  {adminRevokeStudyAccessContent.title}
                </h2>
                <button
                  type="button"
                  aria-label="Close revoke study access"
                  className="flex h-5 w-5 items-center justify-center rounded text-[#394C54] hover:bg-[#EFF1F2]"
                  onClick={onClose}
                >
                  <CloseOutlined />
                </button>
              </div>
            </div>

            <div className="flex h-[210px] flex-col gap-6 bg-white p-4">
              <div className="flex flex-col gap-2">
                <p className="m-0 w-full text-base font-normal leading-[19px] tracking-[-0.16px] text-[#2B4752]">
                  {adminRevokeStudyAccessContent.description}
                </p>
                {submitError ? (
                  <p className="m-0 text-sm text-[#980000]" role="alert">
                    {submitError}
                  </p>
                ) : null}
              </div>

              <div className="grid h-24 w-full grid-cols-[1fr_1fr_1fr_150px] overflow-hidden rounded border border-[#E7E6E4] bg-white shadow-[0px_2px_10px_2px_rgba(0,0,0,0.02)]">
                <div className="contents">
                  <div className="flex h-9 items-center border-b border-[#0000000f] bg-[#F7F7F9] px-2.5 text-sm font-medium leading-6 tracking-[-0.14px] text-[#070B0D]">
                    User
                  </div>
                  <div className="flex h-9 items-center border-b border-[#0000000f] bg-[#F7F7F9] px-2.5 text-sm font-medium leading-6 tracking-[-0.14px] text-[#070B0D]">
                    Study
                  </div>
                  <div className="flex h-9 items-center border-b border-[#0000000f] bg-[#F7F7F9] px-2.5 text-sm font-medium leading-6 tracking-[-0.14px] text-[#070B0D]">
                    Study Access Role
                  </div>
                  <div className="flex h-9 items-center border-b border-[#0000000f] bg-[#F7F7F9] px-2.5 text-sm font-medium leading-6 tracking-[-0.14px] text-[#070B0D]">
                    Access granted
                  </div>
                </div>

                <div className="flex h-[60px] flex-col justify-center border-b border-[#0000000f] bg-white px-2.5 py-2">
                  <span className="text-sm font-normal leading-5 tracking-[-0.14px] text-[#000000d9]">
                    {userName}
                  </span>
                  <span className="text-sm font-normal leading-5 tracking-[-0.14px] text-[#00000073]">
                    {assignment.user.email}
                  </span>
                </div>
                <div className="flex h-[60px] items-center border-b border-[#0000000f] bg-white px-2.5 py-2">
                  <StudyLabelBadge label={studyLabel} />
                </div>
                <div className="flex h-[60px] items-center border-b border-[#0000000f] bg-white px-2.5 py-2">
                  <CompactRoleBadge access={assignment.access} />
                </div>
                <div className="flex h-[60px] items-center border-b border-[#0000000f] bg-white px-2.5 py-3">
                  <span className="text-sm font-normal leading-none tracking-[-0.14px] text-[#0F1923]">
                    {assignment.dateAssigned}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex h-14 items-center justify-between border-t border-[#0000000f] p-3">
            <Button
              className="h-8 rounded border-[#00000073] px-3 font-medium text-[#2B4752]"
              disabled={isSubmitting}
              onClick={onClose}
            >
              {adminRevokeStudyAccessContent.cancelLabel}
            </Button>
            <Button
              danger
              type="primary"
              loading={isSubmitting}
              className="h-8 rounded bg-[#980000]! px-2 font-medium text-white shadow-none hover:bg-[#7f0000]!"
              onClick={handleConfirm}
            >
              {adminRevokeStudyAccessContent.confirmLabel}
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

export default function AdminRolesPermissionsPage() {
  const managedUsersQuery = useAdminManagedUsers();
  const managedTrialsQuery = useAdminManagedTrials();
  const createManagedUserMutation = useCreateManagedUser();
  const updateManagedUserMutation = useUpdateManagedUser();
  const deleteManagedUserMutation = useDeleteManagedUser();
  const managedUsers = useMemo(
    () => managedUsersQuery.data ?? [],
    [managedUsersQuery.data],
  );
  const managedTrials = useMemo(
    () => managedTrialsQuery.data ?? [],
    [managedTrialsQuery.data],
  );
  const studyGroups = useMemo(
    () => mapManagedTrialsToStudyPermissionGroups(managedTrials, managedUsers),
    [managedTrials, managedUsers],
  );
  const adminUsers = useMemo(
    () => mapManagedUsersToAdminUsers(managedUsers, managedTrials),
    [managedUsers, managedTrials],
  );
  const managedStudyOptions = useMemo(
    () => buildStudyOptionsFromManagedTrials(managedTrials),
    [managedTrials],
  );
  const roleOptionsByTrialId = useMemo(
    () => buildTrialRolesById(managedTrials),
    [managedTrials],
  );
  const isLoadingQueries =
    managedUsersQuery.isLoading || managedTrialsQuery.isLoading;
  const queriesError = managedUsersQuery.error ?? managedTrialsQuery.error;
  const [expandedStudyIds, setExpandedStudyIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const {
    contextHolder: editFlowContextHolder,
    editingUser,
    editErrorMessage,
    deactivationIntent,
    deactivateErrorMessage,
    isEditUserSubmitting,
    isDeactivateSubmitting,
    openEditModal,
    closeEditModal,
    closeDeactivationIntent,
    handleEditUserSubmit,
    handleDeactivateUser,
  } = useManagedUserEditFlow();
  const adminCount = useMemo(
    () => managedUsers.filter((user) => user.is_admin).length,
    [managedUsers],
  );
  const [addUserModal, setAddUserModal] =
    useState<AddUserToStudyModalState>(null);
  const [historyDrawer, setHistoryDrawer] =
    useState<AdminAccountHistory | null>(null);
  const [revokeModal, setRevokeModal] = useState<AssignmentActionState>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({});
  const [inviteSuccessMessage, setInviteSuccessMessage] = useState<
    string | null
  >(null);
  const [inviteErrorMessage, setInviteErrorMessage] = useState<string | null>(
    null,
  );

  const usersById = useMemo(
    () => new Map(adminUsers.map((user) => [user.id, user])),
    [adminUsers],
  );
  const availableRoles = useMemo(
    () =>
      Array.from(new Set(managedTrials.flatMap((trial) => trial.roles))).sort(
        (left, right) =>
          formatRoleLabel(left).localeCompare(formatRoleLabel(right)),
      ),
    [managedTrials],
  );
  const rolesFilterMetadata = useMemo(
    () => ({
      [ROLES_FILTER_KEYS.email]: {
        data_type: "text",
        values: Array.from(
          new Set(adminUsers.map((user) => user.email)),
        ).sort(),
      },
      [ROLES_FILTER_KEYS.accountType]: {
        data_type: "text",
        values: ["ADMIN", "USER"],
      },
      [ROLES_FILTER_KEYS.role]: {
        data_type: "text",
        values: availableRoles,
      },
      [ROLES_FILTER_KEYS.trial]: {
        data_type: "text",
        values: studyGroups.map(
          (study) => `${study.studyName} | ${study.trialId}`,
        ),
      },
    }),
    [adminUsers, availableRoles, studyGroups],
  );
  const rolesFilterKeys = useMemo(
    () => Object.keys(rolesFilterMetadata),
    [rolesFilterMetadata],
  );
  const hasActiveFilters = Object.values(selectedFilters).some(
    (values) => Array.isArray(values) && values.length > 0,
  );
  const filteredStudyGroups = useMemo(() => {
    const selectedEmails = new Set(
      (selectedFilters[ROLES_FILTER_KEYS.email] ?? []).map(String),
    );
    const selectedAccountTypes = new Set(
      (selectedFilters[ROLES_FILTER_KEYS.accountType] ?? []).map(String),
    );
    const selectedRoles = new Set(
      (selectedFilters[ROLES_FILTER_KEYS.role] ?? []).map(String),
    );
    const selectedTrials = new Set(
      (selectedFilters[ROLES_FILTER_KEYS.trial] ?? []).map(String),
    );

    return studyGroups
      .map((study) => ({
        ...study,
        assignments: study.assignments.filter((assignment) => {
          if (
            selectedEmails.size > 0 &&
            !selectedEmails.has(assignment.user.email)
          ) {
            return false;
          }

          if (selectedRoles.size > 0 && !selectedRoles.has(assignment.access)) {
            return false;
          }

          if (
            selectedTrials.size > 0 &&
            !selectedTrials.has(`${study.studyName} | ${study.trialId}`)
          ) {
            return false;
          }

          if (selectedAccountTypes.size > 0) {
            const accountType = usersById.get(assignment.user.id)?.accountType;

            if (!accountType || !selectedAccountTypes.has(accountType)) {
              return false;
            }
          }

          return true;
        }),
      }))
      .filter((study) => !hasActiveFilters || study.assignments.length > 0);
  }, [hasActiveFilters, selectedFilters, studyGroups, usersById]);

  useEffect(() => {
    setExpandedStudyIds((current) => {
      const validStudyIds = new Set(
        filteredStudyGroups.map((study) => study.id),
      );
      const nextStudyIds = [...current].filter((studyId) =>
        validStudyIds.has(studyId),
      );

      if (nextStudyIds.length === current.size) {
        return current;
      }

      return new Set(nextStudyIds);
    });
  }, [filteredStudyGroups]);

  const studiesByAssignmentId = useMemo(
    () =>
      new Map(
        filteredStudyGroups.flatMap((study) =>
          study.assignments.map(
            (assignment) => [assignment.id, study] as const,
          ),
        ),
      ),
    [filteredStudyGroups],
  );
  const existingEmails = useMemo(
    () => Array.from(new Set(adminUsers.map((user) => user.email))),
    [adminUsers],
  );
  const buildExportRows = useCallback(
    (studies: AdminStudyPermissionGroup[]) =>
      studies.flatMap((study) =>
        study.assignments.map((assignment) => ({
          study: study.studyName,
          trial_id: study.trialId,
          user: getAssignmentUserName(assignment),
          email: assignment.user.email,
          account_type:
            usersById.get(assignment.user.id)?.accountType ?? "USER",
          study_access_role: formatRoleLabel(assignment.access),
          date_assigned: assignment.dateAssigned,
        })),
      ),
    [usersById],
  );

  const configuredExportRows = useMemo(
    () => buildExportRows(filteredStudyGroups),
    [buildExportRows, filteredStudyGroups],
  );
  const fullExportRows = useMemo(
    () => buildExportRows(studyGroups),
    [buildExportRows, studyGroups],
  );

  const handleOpenInviteModal = () => {
    setInviteSuccessMessage(null);
    setInviteErrorMessage(null);
    setInviteModalOpen(true);
  };

  const clearSelectedFilters = () => {
    setSelectedFilters({});
  };

  const rolesFilterTitleCreator = (filterKey: string) =>
    ROLES_FILTER_LABELS[filterKey] ?? filterKey;

  const handleBatchInviteSubmit = async (
    payload: BatchInvitePayload,
  ): Promise<BatchInviteSubmitResult> => {
    setInviteErrorMessage(null);

    const inviteResults = await Promise.allSettled(
      payload.invites.map((invite) =>
        createManagedUserMutation.mutateAsync(
          buildCreateManagedUserPayload(invite, payload.sharedStudyAssignment),
        ),
      ),
    );

    const failedInvites = payload.invites.filter(
      (_, index) => inviteResults[index]?.status === "rejected",
    );

    if (failedInvites.length === 0) {
      setInviteModalOpen(false);
      setInviteSuccessMessage("Success! Invite sent to new user(s).");
      return {};
    }

    const firstFailure = inviteResults.find(
      (result): result is PromiseRejectedResult => result.status === "rejected",
    );
    const baseMessage =
      firstFailure?.reason instanceof Error
        ? firstFailure.reason.message
        : "Failed to send invite(s). Please try again.";

    setInviteErrorMessage(
      failedInvites.length === payload.invites.length
        ? baseMessage
        : `${baseMessage} Retrying will resend only failed invite(s).`,
    );

    return {
      failedEmails: failedInvites.map((invite) => invite.email),
    };
  };

  const handleAddUsersToStudy = async (
    modalUsers: StudyModalUser[],
    study: AdminStudyPermissionGroup,
  ) => {
    await Promise.all(
      modalUsers.map((user) => {
        const roleAssignment = user.role ?? "full_access";

        if (user.isExistingUser) {
          const existingUser = usersById.get(user.id);

          return updateManagedUserMutation.mutateAsync({
            userId: user.id,
            payload: {
              role_assignment: roleAssignment,
              trials: [study.trialId],
              client_admin: existingUser?.accountType === "ADMIN",
            },
          });
        }

        return createManagedUserMutation.mutateAsync({
          email: user.email,
          role_assignment: roleAssignment,
          trials: [study.trialId],
          client_admin: false,
        });
      }),
    );

    setExpandedStudyIds((current) => new Set(current).add(study.id));
  };

  const handleRevokeStudyAccess = async (
    assignment: AdminStudyAssignment,
    study: AdminStudyPermissionGroup,
  ) => {
    await deleteManagedUserMutation.mutateAsync({
      userId: assignment.user.id,
      options: { trial: study.trialId },
    });
  };

  const toggleStudy = (studyId: string) => {
    setExpandedStudyIds((current) => {
      const next = new Set(current);
      if (next.has(studyId)) {
        next.delete(studyId);
      } else {
        next.add(studyId);
      }
      return next;
    });
  };

  const buildRowMenu = (
    assignment: AdminStudyAssignment,
  ): MenuProps["items"] => [
    {
      key: "edit",
      icon: <EditOutlined style={{ fontSize: 18 }} />,
      label: "Edit user settings",
      onClick: () => {
        const user = usersById.get(assignment.user.id);
        if (user) {
          openEditModal(user);
        }
        setOpenMenuId(null);
      },
    },
    ...(ACCOUNT_HISTORY_ENABLED
      ? [
          {
            key: "history",
            icon: <HistoryOutlined style={{ fontSize: 18 }} />,
            label: "Account history",
            onClick: () => {
              const study = studiesByAssignmentId.get(assignment.id);
              const accountType =
                usersById.get(assignment.user.id)?.accountType ?? "USER";

              setHistoryDrawer(
                adminAccountHistoryByAssignmentId[assignment.id] ??
                  (study
                    ? buildPlaceholderAccountHistory({
                        assignment,
                        study,
                        accountType,
                      })
                    : null),
              );
              setOpenMenuId(null);
            },
          },
        ]
      : []),
    {
      key: "revoke",
      icon: <StopOutlined style={{ color: "#980000", fontSize: 18 }} />,
      label: <span className="text-[#980000]">Revoke study access</span>,
      onClick: () => {
        const study = studiesByAssignmentId.get(assignment.id);
        if (study) {
          setRevokeModal({ assignment, study });
        }
        setOpenMenuId(null);
      },
    },
  ];

  const columns: ColumnsType<AdminStudyAssignment> = [
    {
      title: "User",
      key: "user",
      render: (_, assignment) => {
        const name = getAssignmentUserName(assignment);
        return (
          <div className="flex items-center gap-2">
            <AvatarWithInitials
              name={name}
              avatarSize={32}
              avatarFontSize={14}
              showLabel={false}
            />
            <div className="flex min-w-0 flex-col">
              <span className="text-sm leading-5 text-[#0F1923]">{name}</span>
              <span className="text-sm leading-5 text-[#00000073]">
                {assignment.user.email}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Study Access Role",
      dataIndex: "access",
      key: "access",
      render: (access: AdminStudyAssignment["access"]) => (
        <RoleBadge access={access} />
      ),
    },
    {
      title: "Date Assigned",
      dataIndex: "dateAssigned",
      key: "dateAssigned",
      render: (dateAssigned: string) => (
        <span className="text-base text-[#0F1923]">{dateAssigned}</span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 44,
      render: (_, assignment) => (
        <Dropdown
          menu={{ items: buildRowMenu(assignment) }}
          trigger={["click"]}
          placement="bottomRight"
          open={openMenuId === assignment.id}
          onOpenChange={(open) => setOpenMenuId(open ? assignment.id : null)}
        >
          <button
            type="button"
            aria-label={`Actions for ${getAssignmentUserName(assignment)} in study`}
            className={`flex h-6 w-6 items-center justify-center rounded p-1 transition-colors ${
              openMenuId === assignment.id
                ? "bg-[#EFF1F2] shadow-[0px_2px_10px_2px_rgba(0,0,0,0.05)]"
                : "hover:bg-[#EFF1F2]"
            }`}
          >
            <EllipsisOutlined style={{ color: "#556C75", fontSize: 20 }} />
          </button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="flex w-full flex-col items-start p-5">
      {editFlowContextHolder}
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full justify-between gap-4">
          <PageHeader
            title="Roles & Permissions"
            tooltipText={ROLES_PERMISSIONS_TOOLTIP}
            className="m-0 text-lg font-semibold text-black"
            dataTestId="admin-roles-permissions-header-tooltip"
          />

          <div className="flex items-center gap-2">
            <Button
              type="default"
              icon={<UsergroupAddOutlined style={{ fontSize: 18 }} />}
              className="h-8 rounded border-[#06709B] bg-[#D2EFF9] font-medium text-[#06709B] hover:bg-[#c2e8f6]! hover:text-[#06709B]! hover:border-[#06709B]!"
              onClick={handleOpenInviteModal}
            >
              Invite new user(s)
            </Button>
            <div data-testid="admin-roles-filter">
              <FilterButton
                currentFilters={selectedFilters}
                onFiltersChange={setSelectedFilters}
                allFiltersData={rolesFilterMetadata}
                visibleColumns={rolesFilterKeys}
                columnsTitleCreator={rolesFilterTitleCreator}
              />
            </div>
            <AdminDownloadMenu
              configuredRows={configuredExportRows}
              fullRows={fullExportRows}
              fileName="admin_roles_permissions"
              dataTestId="admin-roles-export"
            />
            <GuideButton pageName="/wiki/guides/admin-roles-permissions" />
          </div>
        </div>

        <AppliedFiltersSection
          filters={selectedFilters}
          setFilters={setSelectedFilters}
          filterOptions={rolesFilterMetadata}
          allFiltersData={rolesFilterMetadata}
          clearFilters={clearSelectedFilters}
          columnsTitleCreator={rolesFilterTitleCreator}
        />

        <div
          className="flex w-full flex-col gap-4"
          data-testid="admin-roles-permissions-studies"
        >
          {isLoadingQueries ? (
            <section className="rounded bg-white p-6 text-sm text-[#556C75] shadow-[2px_2px_5px_rgba(0,0,0,0.03)]">
              Loading roles and permissions...
            </section>
          ) : queriesError ? (
            <section className="flex items-center justify-between rounded bg-white p-6 text-sm text-[#980000] shadow-[2px_2px_5px_rgba(0,0,0,0.03)]">
              <span>
                {queriesError instanceof Error
                  ? queriesError.message
                  : "Failed to load roles and permissions."}
              </span>
              <Button
                onClick={() => {
                  if (managedUsersQuery.error) {
                    void managedUsersQuery.refetch();
                  }
                  if (managedTrialsQuery.error) {
                    void managedTrialsQuery.refetch();
                  }
                }}
              >
                Retry
              </Button>
            </section>
          ) : filteredStudyGroups.length === 0 ? (
            <section className="rounded bg-white p-6 text-sm text-[#556C75] shadow-[2px_2px_5px_rgba(0,0,0,0.03)]">
              {hasActiveFilters
                ? "No matching study assignments found."
                : "No studies available yet."}
            </section>
          ) : (
            filteredStudyGroups.map((study) => {
              const expanded = expandedStudyIds.has(study.id);

              return (
                <section
                  key={study.id}
                  className="overflow-hidden rounded bg-white shadow-[2px_2px_5px_rgba(0,0,0,0.03)]"
                >
                  <div className="flex h-[52px] items-center justify-between border-b border-[#0000000f] px-3">
                    <div className="flex items-center gap-1">
                      <StudyIcon study={study} />
                      <div className="rounded bg-[#0000000a] px-1 py-1 text-base leading-[19px] tracking-[-0.16px] text-[#070B0D]">
                        <span className="font-medium">{study.studyName}</span>
                        <span className="font-normal"> | {study.trialId}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="small"
                        icon={<PlusOutlined />}
                        className="h-6 rounded border-[#0000001f] px-2 text-sm text-[#2B4752]"
                        onClick={() => setAddUserModal({ study })}
                      >
                        Add user to study
                      </Button>
                      <button
                        type="button"
                        aria-label={`${expanded ? "Collapse" : "Expand"} ${study.studyName}`}
                        className="flex h-6 w-6 items-center justify-center rounded text-[#556C75] hover:bg-[#EFF1F2]"
                        onClick={() => toggleStudy(study.id)}
                      >
                        {expanded ? <UpOutlined /> : <DownOutlined />}
                      </button>
                    </div>
                  </div>

                  {expanded ? (
                    <div className="p-4">
                      <Table<AdminStudyAssignment>
                        data-testid={`admin-roles-table-${study.id}`}
                        dataSource={study.assignments}
                        columns={columns}
                        rowKey="id"
                        pagination={false}
                        size="small"
                      />
                    </div>
                  ) : null}
                </section>
              );
            })
          )}
        </div>
      </div>

      <EditUserSettingsModal
        user={editingUser}
        open={editingUser !== null}
        isSubmitting={isEditUserSubmitting}
        submitError={editErrorMessage}
        isLastAdmin={editingUser?.accountType === "ADMIN" && adminCount <= 1}
        roleOptionsByTrialId={roleOptionsByTrialId}
        onClose={closeEditModal}
        onSubmit={handleEditUserSubmit}
      />
      <DeactivateUserModal
        user={deactivationIntent?.user ?? null}
        variant={deactivationIntent?.variant}
        isSubmitting={isDeactivateSubmitting}
        submitError={deactivateErrorMessage}
        onClose={closeDeactivationIntent}
        onConfirm={handleDeactivateUser}
      />
      <AddUserToStudyModal
        modalState={addUserModal}
        users={adminUsers}
        onClose={() => setAddUserModal(null)}
        onSubmit={handleAddUsersToStudy}
      />
      <RevokeStudyAccessModal
        actionState={revokeModal}
        onClose={() => setRevokeModal(null)}
        onConfirm={handleRevokeStudyAccess}
      />
      {ACCOUNT_HISTORY_ENABLED ? (
        <UserHistoryDrawer
          history={historyDrawer}
          open={historyDrawer !== null}
          onClose={() => setHistoryDrawer(null)}
        />
      ) : null}
      <BatchInviteUsersModal
        open={inviteModalOpen}
        existingEmails={existingEmails}
        studyOptions={managedStudyOptions}
        isSubmitting={createManagedUserMutation.isPending}
        submitError={inviteErrorMessage}
        onCancel={() => setInviteModalOpen(false)}
        onSubmit={handleBatchInviteSubmit}
      />

      {inviteSuccessMessage ? (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed bottom-6 left-1/2 z-50 w-[max-content] -translate-x-1/2"
        >
          <div className="flex items-center gap-3 rounded-full border border-[#CAE6D6] bg-[#F0FAF7] px-4 py-3 text-sm font-medium text-[#004A42] shadow-[0px_12px_32px_rgba(0,0,0,0.12)]">
            <CheckCircleFilled className="text-[16px] text-[#0D8C63]" />
            <span>{inviteSuccessMessage}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
